import { useEffect, useRef, useCallback } from "react";
import html2canvas from "html2canvas";

interface ClickScreenshot {
  imageData: string;
  clickX: number;
  clickY: number;
  pagePath: string;
  pageTitle: string;
  viewportWidth: number;
  viewportHeight: number;
  targetSelector: string;
  timestamp: string;
  timeOffset: number;
}

interface PendingUpload {
  screenshot: ClickScreenshot;
  attempts: number;
  lastAttempt: number;
}

const MAX_WIDTH = 1200;
const DEBOUNCE_MS = 500;
const MAX_PAYLOAD_SIZE = 1.8 * 1024 * 1024; // 1.8MB to stay under 2MB limit
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY = 2000;
const QUALITY_STEPS = [0.6, 0.4, 0.25];

function getSelector(element: Element | null): string {
  if (!element || typeof element.getAttribute !== 'function') {
    return '';
  }
  
  if (element.id) {
    return `#${element.id}`;
  }
  
  const testId = element.getAttribute("data-testid");
  if (testId) {
    return `[data-testid="${testId}"]`;
  }
  
  const parts: string[] = [];
  let current: Element | null = element;
  
  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();
    
    if (current.className && typeof current.className === "string") {
      const classes = current.className.trim().split(/\s+/).slice(0, 2).join(".");
      if (classes) {
        selector += `.${classes}`;
      }
    }
    
    parts.unshift(selector);
    current = current.parentElement;
    
    if (parts.length >= 3) break;
  }
  
  return parts.join(" > ");
}

export function useSessionRecorder(sessionToken: string | null, enabled = true) {
  const sessionStartTime = useRef<number>(Date.now());
  const isRecording = useRef(false);
  const lastClickTime = useRef<number>(0);
  const retryQueue = useRef<PendingUpload[]>([]);
  const retryTimerRef = useRef<number | null>(null);
  
  const getTimeOffset = useCallback(() => {
    return Date.now() - sessionStartTime.current;
  }, []);
  
  const sendScreenshot = useCallback(async (
    screenshot: ClickScreenshot,
    isRetry = false
  ): Promise<{ success: boolean; pending: boolean }> => {
    if (!sessionToken) return { success: false, pending: false };
    
    try {
      const response = await fetch("/api/sessions/screenshot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionToken,
          screenshot,
        }),
      });
      
      if (!response.ok) {
        // Use warn - transient failures (504, etc.) are expected and will retry
        console.warn("[Replay] Screenshot upload failed (will retry):", response.status);
        return { success: false, pending: false };
      }
      
      const data = await response.json();
      
      if (data.pending) {
        return { success: false, pending: true };
      }
      
      return { success: true, pending: false };
    } catch (error) {
      // Network errors may not serialize well, extract meaningful info
      const errorInfo = error instanceof Error 
        ? { message: error.message, name: error.name }
        : typeof error === 'string' ? error : 'Unknown network error';
      console.warn("[Replay] Screenshot send failed (will retry):", errorInfo);
      return { success: false, pending: false };
    }
  }, [sessionToken]);
  
  const processRetryQueue = useCallback(async () => {
    if (!sessionToken || retryQueue.current.length === 0) return;
    
    const now = Date.now();
    const toRetry = retryQueue.current.filter(item => {
      const delay = RETRY_BASE_DELAY * Math.pow(2, item.attempts);
      return now - item.lastAttempt >= delay;
    });
    
    for (const item of toRetry) {
      const result = await sendScreenshot(item.screenshot, true);
      
      if (result.success) {
        retryQueue.current = retryQueue.current.filter(q => q !== item);
      } else if (result.pending || item.attempts < MAX_RETRIES) {
        item.attempts++;
        item.lastAttempt = now;
      } else {
        retryQueue.current = retryQueue.current.filter(q => q !== item);
        console.warn("[Replay] Dropping screenshot after max retries");
      }
    }
  }, [sessionToken, sendScreenshot]);
  
  const captureAndSendScreenshot = useCallback(async (
    clickX: number,
    clickY: number,
    targetSelector: string
  ) => {
    if (!sessionToken || !isRecording.current) return;
    
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        allowTaint: true,
        scale: window.devicePixelRatio > 1 ? 1.5 : 1,
        width: Math.min(window.innerWidth, MAX_WIDTH),
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        x: 0,
        y: window.scrollY,
        onclone: (clonedDoc) => {
          // Remove problematic elements that can cause DOM errors
          clonedDoc.querySelectorAll('iframe, video, canvas').forEach(el => el.remove());
        },
        ignoreElements: (element) => {
          return element.hasAttribute('data-replay-ignore') || 
                 element.tagName === 'SCRIPT' || 
                 element.tagName === 'NOSCRIPT' ||
                 element.tagName === 'IFRAME' ||
                 element.tagName === 'VIDEO';
        }
      });
      
      let imageData = '';
      for (const quality of QUALITY_STEPS) {
        imageData = canvas.toDataURL('image/jpeg', quality);
        if (imageData.length <= MAX_PAYLOAD_SIZE) break;
      }
      
      if (imageData.length > MAX_PAYLOAD_SIZE) {
        console.warn("[Replay] Screenshot too large, skipping");
        return;
      }
      
      const screenshot: ClickScreenshot = {
        imageData,
        clickX,
        clickY,
        pagePath: window.location.pathname,
        pageTitle: document.title,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        targetSelector,
        timestamp: new Date().toISOString(),
        timeOffset: getTimeOffset(),
      };
      
      const result = await sendScreenshot(screenshot);
      
      if (!result.success) {
        retryQueue.current.push({
          screenshot,
          attempts: 0,
          lastAttempt: Date.now(),
        });
      }
    } catch (error) {
      // Improve error logging - error objects don't always serialize well
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn("[Replay] Error capturing screenshot:", errorMessage);
    }
  }, [sessionToken, getTimeOffset, sendScreenshot]);
  
  useEffect(() => {
    if (!enabled || !sessionToken) {
      return;
    }
    
    isRecording.current = true;
    sessionStartTime.current = Date.now();
    
    const handleClick = async (e: MouseEvent) => {
      // Defensive: synthetic dispatches / browser-extension events / non-Mouse
      // CustomEvent('click') can reach this listener without a usable event
      // payload. Bail out instead of crashing window.onerror downstream.
      if (!e) return;

      const now = Date.now();
      if (now - lastClickTime.current < DEBOUNCE_MS) return;
      lastClickTime.current = now;

      const target = e.target as Element | null;
      if (!target || !target.isConnected) return;

      const targetSelector = getSelector(target);

      // Snapshot coordinates synchronously. Reading them inside the deferred
      // callback risks `e` or its props being unavailable (extension-injected
      // events, programmatic dispatchEvent of a non-MouseEvent, etc.) and was
      // the root cause of "Cannot read properties of undefined (reading
      // 'clientX')" in production. clientX/clientY default to 0 for
      // dispatchEvent-style synthetic clicks, which is fine for replay.
      const clickX = typeof e.clientX === "number" ? e.clientX : 0;
      const clickY = typeof e.clientY === "number" ? e.clientY : 0;

      setTimeout(() => {
        captureAndSendScreenshot(clickX, clickY, targetSelector);
      }, 100);
    };
    
    document.addEventListener("click", handleClick, { capture: true });
    
    setTimeout(() => {
      captureAndSendScreenshot(0, 0, "initial_load");
    }, 1000);
    
    retryTimerRef.current = window.setInterval(() => {
      processRetryQueue();
    }, 5000);
    
    return () => {
      isRecording.current = false;
      document.removeEventListener("click", handleClick, { capture: true });
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current);
      }
    };
  }, [enabled, sessionToken, captureAndSendScreenshot, processRetryQueue]);
  
  return {
    captureSnapshot: () => captureAndSendScreenshot(0, 0, "manual_capture"),
    flushEvents: async () => {},
    isRecording: isRecording.current,
  };
}
